import { CampaignAdsGenerateSpinner } from '@/components/campaign/overview/ads-generate-spinner'
import useAreAllTasksCompleted from '@/hooks/useAreAllTasksCompleted'
import {
  campaignQueryOptions,
  campaignTaskTrackingQueryOptions,
} from '@/lib/query-options'
import { campaignTaskTrackingAtom } from '@/store'
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useSetAtom } from 'jotai'
import { useEffect, useState, useRef } from 'react'

export const Route = createFileRoute(
  '/_authenticated/new/_layout/deploying/$slug',
)({
  component: CampaignDeployingComponent,
})

function CampaignDeployingComponent() {
  const { slug } = Route.useParams()
  const [minTimeReached, setMinTimeReached] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const queryClient = useQueryClient()

  const { data: campaign } = useSuspenseQuery(campaignQueryOptions(slug))
  const setCampaignTaskTracking = useSetAtom(campaignTaskTrackingAtom)
  const { data: campaignTaskTracking } = useQuery(
    campaignTaskTrackingQueryOptions(campaign.id),
  )

  setCampaignTaskTracking(campaignTaskTracking)
  const areAllTasksCompleted = useAreAllTasksCompleted()

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }

    if (!campaignTaskTracking || !campaignTaskTracking.created_at) {
      return
    }

    const taskCreationTime = new Date(campaignTaskTracking.created_at).getTime()
    const oneMinuteAfterCreation = taskCreationTime + 60000
    const currentTime = Date.now()

    if (currentTime >= oneMinuteAfterCreation) {
      setMinTimeReached(true)
    } else {
      const timeRemaining = oneMinuteAfterCreation - currentTime

      timerRef.current = setTimeout(() => {
        setMinTimeReached(true)
      }, timeRemaining)
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [campaignTaskTracking])

  const navigate = useNavigate()
  useEffect(() => {
    if (areAllTasksCompleted && minTimeReached) {
      // Invalidate notifications so the deployment notification appears
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notificationCounts'] })
      navigate({ to: `/campaigns/google/${slug}` })
    }
  }, [
    areAllTasksCompleted,
    minTimeReached,
    campaignTaskTracking,
    slug,
    navigate,
    queryClient,
  ])

  const handleBackClick = () => {
    navigate({ to: '/campaigns' })
  }

  return (
    <CampaignAdsGenerateSpinner
      showBackButton={true}
      onBackClick={handleBackClick}
      backButtonText="Back to Campaigns"
    />
  )
}
